import './DownloadDropdown.scss';
import cn from 'classnames';
import { ComponentProps, useContext, useMemo, useState } from 'react';
import { Dropdown as BSDropdown } from 'react-bootstrap';
import DropdownContext from '@restart/ui/DropdownContext';
import {
  Appearance,
  AppearanceType,
  Background,
  BackgroundType,
  Design,
  DesignType,
  Size,
  SizeType
} from '../designConstants';
import { DownloadToggle } from './DownloadToggle';

interface DownloadDropdownProps {
  appearance?: AppearanceType;
  design?: DesignType;
  background?: BackgroundType;
  onClickOptions: (() => void)[];
  menuOptions?: string[];
  disabledMenuOptions?: Record<string, boolean>;
  size?: SizeType;
  disabled?: boolean;
  align?: 'start' | 'end';
}

const MENU_OPTIONS = ['XLSX', 'PNG'];

// Pulls react-bootstrap's setToggle ref out of DropdownContext so Popper can
// use the toggle as its positioning reference. Required for align='end' (and
// any other popper-driven layout) because DownloadToggle bypasses BSDropdown.Toggle.
const ToggleWithContextRef = (props: Omit<ComponentProps<typeof DownloadToggle>, 'buttonRef'>) => {
  const ctx = useContext(DropdownContext);
  return <DownloadToggle {...props} buttonRef={ctx?.setToggle} />;
};

export const DownloadDropdown = ({
  appearance = Appearance.FILLED,
  design = Design.NEUTRAL,
  background = Background.LIGHT,
  onClickOptions,
  menuOptions = MENU_OPTIONS,
  disabledMenuOptions = { XLSX: false, PNG: false },
  size = Size.MD,
  disabled,
  align = 'start'
}: DownloadDropdownProps) => {
  const [show, setShow] = useState(false);

  const enabledMenuOptions = useMemo(
    () =>
      menuOptions
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => !disabledMenuOptions[option]),
    [menuOptions, disabledMenuOptions]
  );

  if (enabledMenuOptions.length === 1) {
    const { index } = enabledMenuOptions[0];
    return (
      <DownloadToggle
        appearance={appearance}
        design={design}
        background={background}
        size={size}
        disabled={disabled}
        onToggleClick={() => onClickOptions[index]()}
        isOpen={false}
      />
    );
  }

  return (
    <BSDropdown className='download' show={show} onToggle={setShow}>
      <ToggleWithContextRef
        appearance={appearance}
        design={design}
        background={background}
        size={size}
        disabled={disabled}
        onToggleClick={() => setShow(s => !s)}
        isOpen={show}
      />
      <BSDropdown.Menu className={cn('download-menu', size)} align={align}>
        {enabledMenuOptions.map(({ option, index }) => (
          <BSDropdown.Item key={option} onClick={() => onClickOptions[index]()}>
            {option}
          </BSDropdown.Item>
        ))}
      </BSDropdown.Menu>
    </BSDropdown>
  );
};
